import {
  FIELDS,
  RepaymentStrategyField,
  Borrower,
  CreditRecord,
  Loan,
  Summary,
  MortgageProperty,
  Income,
  Assets,
  Expenditure,
  Affordability,
  SpecialConditions,
  Approvals,
  RepaymentStrategy,
} from 'modules/cm/domain/Underwriting/Underwriting';
import formatCurrencyValue from 'utils/formatCurrency';
import { format } from 'utils/date';
import { formatPercentage } from 'utils/text';
import { makeBooleanValue } from 'modules/cm/pages/CaseDetailsPage/partials/UnderwritingSummary/viewModel/mappers';
import { ProductType, ProductTypes } from 'domain/Product';
import { productTypeChecker } from 'modules/cm/hooks/useProductType';

export const makeValue = (value: any): string => {
  return value || '-';
};

const makeCurrency = (value: any): string => {
  return value ? formatCurrencyValue(value) : makeValue(value);
};

const makeDate = (value: any): string => {
  return value ? format(value).fromBackendToView() : makeValue(value);
};

const makePercentage = (value: any): string => {
  return value ? formatPercentage(value) : makeValue(value);
};

const makeBoolean = (value: any): string => {
  return typeof value === 'boolean' ? makeBooleanValue(value) : makeValue(value);
};

export type Labelled = { mapper: (value: any) => string; label: string };

export const SummaryFields: Record<keyof Summary, Labelled> = {
  [FIELDS.APPLICATION_DATE]: {
    mapper: makeDate,
    label: 'Application Date',
  },
  [FIELDS.APPLICATION_ID]: {
    mapper: (value: any) => makeValue(value),
    label: 'Application ID',
  },
  [FIELDS.APPLICANT_SURNAME]: {
    mapper: (value: any) => makeValue(value),
    label: 'Applicant Surname',
  },
  [FIELDS.CREDIT_POLICY_EXCEPTION_RATIONALE]: {
    mapper: (value: any) => makeValue(value),
    label: 'Summary of Credit Policy Exception',
  },
  [FIELDS.DELEGATED_AUTHORITY_LEVEL]: {
    mapper: (value: any) => makeValue(value),
    label: 'Delegated Authority Level',
  },
  [FIELDS.FIRM_NAME]: {
    mapper: (value: any) => makeValue(value),
    label: 'Firm Name',
  },
  [FIELDS.HAS_ADVERSE_CREDIT]: {
    mapper: makeBoolean,
    label: 'Adverse credit',
  },
  [FIELDS.IS_CREDIT_POLICY_EXEMPTION]: {
    mapper: makeBoolean,
    label: 'Credit Policy Exception',
  },
  [FIELDS.UNDERWRITER]: {
    mapper: (value: any) => makeValue(value),
    label: 'Underwriter',
  },
  [FIELDS.UNDERWRITER_RECOMMENDATION]: {
    mapper: (value: any) => makeValue(value),
    label: 'Recommendation',
  },
};

const SingleBorrowerFields: Record<keyof Borrower[FIELDS.APPLICANT_1], Labelled> = {
  [FIELDS.FIRST_NAME]: { mapper: (value: any) => makeValue(value), label: 'Applicant First Name' },
  [FIELDS.LAST_NAME]: { mapper: (value: any) => makeValue(value), label: 'Applicant Surname' },
  [FIELDS.GENDER]: { mapper: (value: any) => makeValue(value), label: 'Gender' },
  [FIELDS.AGE]: { mapper: (value: any) => makeValue(value), label: 'Age' },
  [FIELDS.NATIONALITY]: { mapper: (value: any) => makeValue(value), label: 'Nationality' },
  [FIELDS.MARITAL_STATUS]: { mapper: (value: any) => makeValue(value), label: 'Marital Status' },
  [FIELDS.POWER_OF_ATTORNEY_FULL_NAME]: { mapper: (value: any) => makeValue(value), label: 'Power of Attorney' },
  [FIELDS.EMPLOYMENT_STATUS]: { mapper: (value: any) => makeValue(value), label: 'Employment Status' },
  [FIELDS.PLANNED_RETIREMENT_AGE]: { mapper: (value: any) => makeValue(value), label: 'Planned Retirement Age' },
  [FIELDS.PROOF_OF_ID]: { mapper: (value: any) => makeValue(value), label: 'Proof of ID' },
  [FIELDS.PROOF_OF_ADDRESS]: { mapper: (value: any) => makeValue(value), label: 'Proof of Address' },
};

export const BorrowerFields: Record<keyof Borrower, any> = {
  [FIELDS.APPLICANT_1]: SingleBorrowerFields,
  [FIELDS.APPLICANT_2]: SingleBorrowerFields,
};

const SingleApplicantCreditRecordFields: Record<keyof CreditRecord[FIELDS.APPLICANT_1], Labelled> = {
  [FIELDS.CCJ_AMOUNT]: { mapper: (value: any) => makeValue(value), label: 'CCJ Amount' },
  [FIELDS.CCJ_LATEST_DATE]: { mapper: (value: any) => makeValue(value), label: 'CCJ Latest date' },
  [FIELDS.DEFAULT_AMOUNT]: { mapper: makeCurrency, label: 'Default Amount' },
  [FIELDS.DEFAULT_LATEST_DATE]: { mapper: (value: any) => makeValue(value), label: 'Default Latest Date' },
  [FIELDS.MORTGAGE_ARREARS_AMOUNT]: { mapper: makeCurrency, label: 'Mortgage Arrears Amount' },
  [FIELDS.MORTGAGE_ARREARS_LATEST_DATE]: {
    mapper: (value: any) => makeValue(value),
    label: 'Mortgage Arrears Latest Date',
  },
  [FIELDS.BANKRUPTCY_AMOUNT]: { mapper: makeCurrency, label: 'Bankruptcy Amount' },
  [FIELDS.BANKRUPTCY_LATEST_DATE]: { mapper: (value: any) => makeValue(value), label: 'Bankruptcy Latest Date' },
  [FIELDS.HAS_CRIMINAL_CONVICTIONS]: { mapper: (value: any) => makeValue(value), label: 'Criminal convictions' },
  [FIELDS.CRIMINAL_CONVICTIONS_DETAILS]: { mapper: (value: any) => makeValue(value), label: 'Details' },
  [FIELDS.HAS_ADVERSE_CREDIT]: { mapper: (value: any) => makeValue(value), label: 'Adverse credit' },
  [FIELDS.ADVERSE_CREDIT_DETAILS]: { mapper: (value: any) => makeValue(value), label: 'Details' },
};

export const CreditRecordFields: Record<keyof CreditRecord, any> = {
  [FIELDS.APPLICANT_1]: SingleApplicantCreditRecordFields,
  [FIELDS.APPLICANT_2]: SingleApplicantCreditRecordFields,
  [FIELDS.DELPHI_SCORE]: { mapper: (value: any) => makeValue(value), label: 'Delphi Score' },
};

export const LoanFields: Record<keyof Omit<Loan, FIELDS.PRODUCT_TYPE>, Labelled> = {
  [FIELDS.PRODUCT_NAME]: { mapper: (value: any) => makeValue(value), label: 'Product name' },
  [FIELDS.FIXED_INTEREST_RATE]: { mapper: (value: any) => makeValue(value), label: 'Fixed Interest Rate' },
  [FIELDS.PRODUCT_TERM_LENGTH]: { mapper: makeValue, label: 'Term Length' },
  [FIELDS.LOAN_PURPOSE]: { mapper: (value: any) => makeValue(value), label: 'Loan Purpose' },
  [FIELDS.LOAN_PURCHASE]: { mapper: makeCurrency, label: 'Purchase' },
  [FIELDS.LOAN_REMORTGAGE]: { mapper: makeCurrency, label: 'Re-mortgage' },
  [FIELDS.DEBT_CONSOLIDATION]: { mapper: makeCurrency, label: 'Debt consolidation' },
  [FIELDS.HOME_IMPROVEMENTS]: { mapper: makeCurrency, label: 'Home Improvements' },
  [FIELDS.DISCRETIONARY_SPEND]: { mapper: makeCurrency, label: 'Discretionary Spend' },
  [FIELDS.ASSISTING_OTHERS]: { mapper: makeCurrency, label: 'Assisting Others' },
  [FIELDS.OTHER_PURPOSES]: { mapper: makeCurrency, label: 'Others' },
  [FIELDS.NET_LOAN]: { mapper: makeCurrency, label: 'Net Loan' },
  [FIELDS.GROSS_LOAN]: { mapper: makeCurrency, label: 'Gross Loan' },
  [FIELDS.LOAN_TO_VALUE]: { mapper: makePercentage, label: 'LTV ' },
  [FIELDS.DEBT_TO_INCOME]: { mapper: makeValue, label: 'Debt to Income' },
};

export const MortgagePropertyFields: Record<keyof MortgageProperty, any> = {
  [FIELDS.FLAT_NUMBER]: { mapper: (value: any) => makeValue(value), label: 'Flat Number' },
  [FIELDS.BUILDING_NUMBER]: { mapper: (value: any) => makeValue(value), label: 'Building Number' },
  [FIELDS.BUILDING_NAME]: { mapper: (value: any) => makeValue(value), label: 'Building Name' },
  [FIELDS.STREET]: { mapper: (value: any) => makeValue(value), label: 'Street' },
  [FIELDS.CITY]: { mapper: (value: any) => makeValue(value), label: 'City' },
  [FIELDS.REGION]: { mapper: (value: any) => makeValue(value), label: 'Region' },
  [FIELDS.POSTCODE]: { mapper: (value: any) => makeValue(value), label: 'Postcode' },
  [FIELDS.TYPE]: { mapper: (value: any) => makeValue(value), label: 'Property Type' },
  [FIELDS.TENURE]: { mapper: (value: any) => makeValue(value), label: 'Property Tenure' },
  [FIELDS.REMAINING_LEASE_YEARS]: { mapper: (value: any) => makeValue(value), label: 'Remaining Term' },
  [FIELDS.VALUATION_TYPE]: { mapper: (value: any) => makeValue(value), label: 'Type of Valuation' },
  [FIELDS.VALUATION_INSPECTION_DATE]: { mapper: makeDate, label: 'Date of Valuation' },
  [FIELDS.VALUATION_ESTIMATED_VALUE]: { mapper: makeCurrency, label: 'Valuation Estimate' },
  [FIELDS.VALUATION_CONFIRMED_VALUE]: { mapper: makeCurrency, label: 'Confirmed Valuation' },
};

export const IncomeFields: Record<keyof Income, any> = {
  [FIELDS.CURRENT_INCOME]: {
    [FIELDS.EMPLOYMENT_INCOME]: { mapper: makeCurrency, label: 'Employment Income' },
    [FIELDS.SELF_EMPLOYMENT_INCOME]: { mapper: makeCurrency, label: 'Self-Employed Income' },
    [FIELDS.OTHER_INCOME]: { mapper: makeCurrency, label: 'Other Income' },
    [FIELDS.PENSION_INCOME]: { mapper: makeCurrency, label: 'Pension Income' },
    [FIELDS.TOTAL_INCOME]: { mapper: makeCurrency, label: 'Total Current Income' },
    [FIELDS.COMBINED_INCOME]: { mapper: makeCurrency, label: 'Combined Current Income' },
  },
  [FIELDS.FUTURE_INCOME]: {
    [FIELDS.EMPLOYMENT_INCOME]: { mapper: makeCurrency, label: 'Employment Income' },
    [FIELDS.SELF_EMPLOYMENT_INCOME]: { mapper: makeCurrency, label: 'Self-Employed Income' },
    [FIELDS.OTHER_INCOME]: { mapper: makeCurrency, label: 'Other Income ' },
    [FIELDS.PENSION_INCOME]: { mapper: makeCurrency, label: 'Pension Income' },
    [FIELDS.TOTAL_INCOME]: { mapper: makeCurrency, label: 'Total Future Income' },
    [FIELDS.COMBINED_INCOME]: { mapper: makeCurrency, label: 'Combined Future Income' },
  },
  [FIELDS.SURVIVOR]: {
    [FIELDS.SURVIVOR_INCOME]: {
      mapper: makeCurrency,
      label: 'Survivor Income',
    },
  },
};

const ApplicantAssetFields: Record<keyof Assets[FIELDS.APPLICANT_1][0], Labelled> = {
  [FIELDS.ASSET_TYPE]: { mapper: (value: any) => makeValue(value), label: 'Type' },
  [FIELDS.ASSET_VALUE]: { mapper: makeCurrency, label: 'Value' },
  [FIELDS.ASSET_DESCRIPTION]: { mapper: (value: any) => makeValue(value), label: 'Description' },
};

export const AssetsFields: Record<keyof Assets, any> = {
  [FIELDS.APPLICANT_1]: ApplicantAssetFields,
  [FIELDS.APPLICANT_2]: ApplicantAssetFields,
};

const CommitmentFields: Record<keyof Expenditure['applicant1']['monthly'], Labelled> = {
  [FIELDS.COMMITMENT_TO_BE_CLEARED]: { mapper: makeCurrency, label: 'Commitments to be cleared' },
  [FIELDS.COMMITMENT_REMAINING]: { mapper: makeCurrency, label: 'Commitments remaining' },
  [FIELDS.COMMITMENT_TOTAL]: { mapper: makeCurrency, label: 'Total' },
};

export const ExpenditureFields: Record<keyof Expenditure, any> = {
  [FIELDS.APPLICANT_1]: {
    [FIELDS.COMMITMENT_BALANCE]: CommitmentFields,
    [FIELDS.COMMITMENT_MONTHLY]: CommitmentFields,
  },
  [FIELDS.APPLICANT_2]: {
    [FIELDS.COMMITMENT_BALANCE]: CommitmentFields,
    [FIELDS.COMMITMENT_MONTHLY]: CommitmentFields,
  },
  [FIELDS.COMMITMENT_COMBINED_MONTHLY_BASIC_SPEND]: {
    mapper: (value: any) => makeValue(value),
    label: 'Monthly Basic Spend (combined)',
  },
};

export const AffordabilityFields: Record<keyof Affordability, Labelled> = {
  [FIELDS.MAXIMUM_LOAN_POSSIBLE]: { mapper: makeCurrency, label: 'Maximum Loan Possible' },
  [FIELDS.DISPOSABLE_INCOME]: { mapper: makeCurrency, label: 'Disposable Income' },
  [FIELDS.DISPOSABLE_INCOME_BUFFER]: { mapper: makeCurrency, label: 'Disposable Income Buffer' },
  [FIELDS.STAGE]: { mapper: (value: any) => makeValue(value), label: 'Stage' },
  [FIELDS.LIVING_COSTS]: { mapper: makeCurrency, label: 'Living Costs' },
  [FIELDS.NET_CASHFLOW]: { mapper: makeCurrency, label: 'Net Cashflow' },
};

export const SpecialConditionsFields: Record<keyof SpecialConditions, Labelled> = {
  [FIELDS.SPECIAL_CONDITIONS]: { mapper: (value: any) => makeValue(value), label: 'Special conditions' },
};

export const ApprovalsFields: Record<keyof Approvals, Labelled> = {
  [FIELDS.INITIAL_ASSESSMENT_BY]: { mapper: (value: any) => makeValue(value), label: 'Initial Assessment By' },
  [FIELDS.INITIAL_ASSESSMENT_DATE]: { mapper: makeDate, label: 'Date' },
  [FIELDS.APPROVED_BY]: { mapper: (value: any) => makeValue(value), label: 'Approved By:' },
  [FIELDS.APPROVED_DATE]: { mapper: makeDate, label: 'Date:' },
};

export const getRepaymentStrategyFields = (productType: ProductType): Record<keyof RepaymentStrategy, Labelled> => {
  const isTIO = productTypeChecker(ProductTypes.TIO)(productType);

  return {
    [RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing]: {
      mapper: (value: any) => (isTIO ? (value == null ? makeValue(value) : makeBooleanValue(value)) : makeValue(null)),
      label: 'Sale of property/downsizing',
    },
    [RepaymentStrategyField.HaveSaleOfAnotherProperty]: {
      mapper: (value: any) => (isTIO ? (value == null ? makeValue(value) : makeBooleanValue(value)) : makeValue(null)),
      label: 'Sale of another property',
    },
    [RepaymentStrategyField.HaveInvestments]: {
      mapper: (value: any) => (isTIO ? (value == null ? makeValue(value) : makeBooleanValue(value)) : makeValue(null)),
      label: 'Investments',
    },
    [RepaymentStrategyField.HaveEndownments]: {
      mapper: (value: any) => (isTIO ? (value == null ? makeValue(value) : makeBooleanValue(value)) : makeValue(null)),
      label: 'Endowments',
    },
    [RepaymentStrategyField.HaveOther]: {
      mapper: (value: any) => (isTIO ? (value == null ? makeValue(value) : makeBooleanValue(value)) : makeValue(null)),
      label: 'Other',
    },
  };
};
